import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { ONBOARDING } from '@/constants/testIds';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SkillStars from '@/components/SkillStars';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Sparkles, Loader2, Wand2, ImagePlus, Plus, Trash2, Trophy, Users, Clock, Plane, Instagram, Youtube, Music2, Facebook, Twitter,
} from 'lucide-react';
import { toast } from 'sonner';
import VerifySocialButton from '@/components/VerifySocialButton';
import { CalendarManager } from '@/components/Calendar';

const csv = (s) => s.split(',').map(x => x.trim()).filter(Boolean);

const TRAVEL_OPTS = [
  { value: 'city', label: 'City only' },
  { value: 'state', label: 'Within state' },
  { value: 'country', label: 'Across India' },
  { value: 'international', label: 'International (worldwide)' },
];

const SOCIAL_PLATFORMS = [
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-400', followerLabel: 'Followers' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: 'text-rose-400', followerLabel: 'Subscribers' },
  { id: 'tiktok', label: 'TikTok', icon: Music2, color: 'text-cyan-300', followerLabel: 'Followers' },
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-400', followerLabel: 'Followers' },
  { id: 'x', label: 'X / Twitter', icon: Twitter, color: 'text-slate-300', followerLabel: 'Followers' },
];

const Onboarding = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    stage_name: '', bio: '', categories: '', skills: '', languages: '',
    location_city: '', location_country: 'India', experience_years: 0,
    price_min: 0, price_max: 0,
    profile_image: '', cover_image: '',
    embed_input: '', testimonial_input: '',
    ai_notes: '',
    team_size: 1, show_duration_min: 60, travel_scope: 'country',
    performance_languages: '',
    social_links: { instagram: '', youtube: '', tiktok: '', facebook: '', x: '' },
    social_followers: { instagram: 0, youtube: 0, tiktok: 0, facebook: 0, x: 0 },
  });
  const [skillRatings, setSkillRatings] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [socialVerified, setSocialVerified] = useState({});
  const [artistId, setArtistId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/artists/me/profile').then(({ data }) => {
      const a = data.artist;
      if (!a) return;
      setForm((f) => ({
        ...f,
        stage_name: a.stage_name || '', bio: a.bio || '',
        categories: (a.categories || []).join(', '),
        skills: (a.skills || []).join(', '),
        languages: (a.languages || []).join(', '),
        location_city: a.location_city || '', location_country: a.location_country || 'India',
        experience_years: a.experience_years || 0,
        price_min: a.price_min || 0, price_max: a.price_max || 0,
        profile_image: a.profile_image || '', cover_image: a.cover_image || '',
        embed_input: (a.embed_urls || []).join('\n'),
        testimonial_input: (a.testimonial_video_urls || []).join('\n'),
        team_size: a.team_size || 1,
        show_duration_min: a.show_duration_min || 60,
        travel_scope: a.travel_scope || 'country',
        performance_languages: (a.performance_languages || []).join(', '),
        social_links: { instagram: '', youtube: '', tiktok: '', facebook: '', x: '', ...(a.social_links || {}) },
        social_followers: { instagram: 0, youtube: 0, tiktok: 0, facebook: 0, x: 0, ...(a.social_followers || {}) },
      }));
      setSkillRatings(a.skill_ratings || []);
      setMilestones(a.milestones || []);
      setPortfolio(a.portfolio_images || []);
      setSocialVerified(a.social_verified || {});
      setArtistId(a.artist_id);
    }).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateSocial = (platform, field) => (e) => {
    const val = field === 'followers' ? Number(e.target.value) || 0 : e.target.value;
    const key = field === 'followers' ? 'social_followers' : 'social_links';
    setForm((f) => ({ ...f, [key]: { ...f[key], [platform]: val } }));
  };

  const aiGenerate = async () => {
    if (!form.stage_name) { toast.error('Please enter your stage name first'); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post('/ai/portfolio', {
        stage_name: form.stage_name,
        social_links: form.social_links,
        categories: csv(form.categories),
        notes: form.ai_notes,
      });
      setForm((f) => ({
        ...f,
        bio: data.bio || f.bio,
        skills: data.tags?.join(', ') || f.skills,
        languages: data.languages?.join(', ') || f.languages,
        categories: data.suggested_categories?.join(', ') || f.categories,
      }));
      toast.success('AI portfolio generated', { description: 'Review & save when ready.' });
    } catch (err) {
      toast.error('AI generation failed', { description: err?.response?.data?.detail });
    } finally { setAiLoading(false); }
  };

  const upload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      if (target === 'profile') setForm((f) => ({ ...f, profile_image: url }));
      else if (target === 'cover') setForm((f) => ({ ...f, cover_image: url }));
      else if (target?.startsWith?.('milestone-')) {
        const idx = Number(target.split('-')[1]);
        setMilestones((ms) => ms.map((m, i) => (i === idx ? { ...m, image_url: url } : m)));
      } else setPortfolio((p) => [...p, url]);
      toast.success('Uploaded');
    } catch {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const addSkillRating = () => setSkillRatings((s) => [...s, { name: '', rating: 3 }]);
  const updateSkillRating = (i, field, val) =>
    setSkillRatings((s) => s.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const removeSkillRating = (i) => setSkillRatings((s) => s.filter((_, idx) => idx !== i));

  const addMilestone = () => setMilestones((m) => [...m, { title: '', description: '', year: '', image_url: '' }]);
  const updateMilestone = (i, field, val) =>
    setMilestones((m) => m.map((mi, idx) => (idx === i ? { ...mi, [field]: val } : mi)));
  const removeMilestone = (i) => setMilestones((m) => m.filter((_, idx) => idx !== i));

  const removePortfolio = (i) => setPortfolio((p) => p.filter((_, idx) => idx !== i));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        stage_name: form.stage_name, bio: form.bio,
        categories: csv(form.categories), skills: csv(form.skills), languages: csv(form.languages),
        location_city: form.location_city, location_country: form.location_country,
        experience_years: Number(form.experience_years) || 0,
        price_min: Number(form.price_min) || 0, price_max: Number(form.price_max) || 0,
        profile_image: form.profile_image, cover_image: form.cover_image,
        social_links: form.social_links,
        social_followers: form.social_followers,
        embed_urls: form.embed_input.split('\n').map(s => s.trim()).filter(Boolean),
        testimonial_video_urls: form.testimonial_input.split('\n').map(s => s.trim()).filter(Boolean),
        portfolio_images: portfolio,
        skill_ratings: skillRatings.filter((s) => s.name?.trim()).map((s) => ({ name: s.name.trim(), rating: Number(s.rating) || 0 })),
        milestones: milestones.filter((m) => m.title?.trim()),
        team_size: Number(form.team_size) || 1,
        show_duration_min: Number(form.show_duration_min) || 0,
        travel_scope: form.travel_scope,
        performance_languages: csv(form.performance_languages),
      };
      await api.post('/artists/me', payload);
      toast.success('Profile saved!', { description: 'Your artist profile is live.' });
    } catch (err) {
      toast.error('Could not save', { description: err?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-24 max-w-4xl mx-auto px-6 sm:px-12">
      <div className="text-center mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-cyan-300 mb-3">Become an artist</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4">
          Build your <span className="ai-text">AI portfolio</span>
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto">Paste your social links, write a few notes, and let Claude craft a cinematic bio for you.</p>
      </div>

      <form onSubmit={save} className="space-y-8">
        {/* AI Section */}
        <div className="glass-strong rounded-2xl p-7 border border-cyan-500/20">
          <div className="flex items-center gap-2 mb-4">
            <Wand2 className="h-5 w-5 text-cyan-300" />
            <h2 className="font-display text-xl text-white">AI Portfolio Builder</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Stage name *</Label>
              <Input value={form.stage_name} onChange={update('stage_name')} required data-testid={ONBOARDING.stageNameInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Categories (comma-separated)</Label>
              <Input value={form.categories} onChange={update('categories')} placeholder="Singer, Musician" data-testid={ONBOARDING.categoriesInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
          </div>
          <Label className="text-xs text-slate-400 uppercase tracking-wider">Notes for AI (optional)</Label>
          <Textarea value={form.ai_notes} onChange={update('ai_notes')} rows={3} placeholder="e.g. 8 years experience, specialize in luxury weddings, performed at Sunburn…" data-testid={ONBOARDING.aiNotesInput} className="bg-white/5 border-white/10 mt-1 mb-4" />
          <Button type="button" onClick={aiGenerate} disabled={aiLoading} data-testid={ONBOARDING.aiGenerateButton} className="btn-ai text-white border-0 rounded-full px-6">
            {aiLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating…</> : <><Sparkles className="h-4 w-4 mr-2" />Generate with AI</>}
          </Button>
        </div>

        {/* Media */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <h2 className="font-display text-xl text-white mb-4">Media</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Profile image URL</Label>
              <Input value={form.profile_image} onChange={update('profile_image')} placeholder="https://…" data-testid={ONBOARDING.profileImageInput} className="bg-white/5 border-white/10 mt-1" />
              <label className="mt-2 inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 cursor-pointer">
                <ImagePlus className="h-3.5 w-3.5" /> upload file
                <input type="file" accept="image/*" hidden onChange={(e) => upload(e, 'profile')} disabled={uploading} />
              </label>
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Cover image URL</Label>
              <Input value={form.cover_image} onChange={update('cover_image')} placeholder="https://…" data-testid={ONBOARDING.coverImageInput} className="bg-white/5 border-white/10 mt-1" />
              <label className="mt-2 inline-flex items-center gap-2 text-xs text-cyan-300 hover:text-cyan-200 cursor-pointer">
                <ImagePlus className="h-3.5 w-3.5" /> upload file
                <input type="file" accept="image/*" hidden onChange={(e) => upload(e, 'cover')} disabled={uploading} />
              </label>
            </div>
          </div>
        </div>

        {/* Skill Ratings */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-display text-xl text-white flex items-center gap-2"><Sparkles className="h-5 w-5 text-fuchsia-300" /> Rate your skills</h2>
            <Button type="button" onClick={addSkillRating} variant="outline" data-testid="onboarding-add-skill-rating" className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-200 rounded-full">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add skill
            </Button>
          </div>
          <p className="text-xs text-slate-400 mb-4">Rate yourself 0–5 on your top skills. Hosts see these as progressive stars on your public profile.</p>
          {skillRatings.length === 0 && <div className="text-sm text-slate-500 italic">No rated skills yet. Click &quot;Add skill&quot; to start.</div>}
          <div className="space-y-3">
            {skillRatings.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-3">
                <Input
                  value={s.name}
                  onChange={(e) => updateSkillRating(i, 'name', e.target.value)}
                  placeholder="e.g. Bharatanatyam, Live Sound, Stand-up…"
                  data-testid={`onboarding-skill-rating-name-${i}`}
                  className="bg-white/5 border-white/10"
                />
                <SkillStars value={s.rating} onChange={(v) => updateSkillRating(i, 'rating', v)} interactive size="md" testIdPrefix={`onboarding-skill-rating-${i}`} />
                <button type="button" onClick={() => removeSkillRating(i)} aria-label="Remove" className="text-slate-500 hover:text-rose-300 transition-colors p-2">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Details */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Users className="h-5 w-5 text-cyan-300" /> Performance details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><Users className="h-3 w-3" /> Team size (incl. off-stage)</Label>
              <Input type="number" min="1" value={form.team_size} onChange={update('team_size')} data-testid="onboarding-team-size-input" className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><Clock className="h-3 w-3" /> Show duration (minutes)</Label>
              <Input type="number" min="0" value={form.show_duration_min} onChange={update('show_duration_min')} data-testid="onboarding-show-duration-input" className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider flex items-center gap-1.5"><Plane className="h-3 w-3" /> Travel scope</Label>
              <Select value={form.travel_scope} onValueChange={(v) => setForm((f) => ({ ...f, travel_scope: v }))}>
                <SelectTrigger data-testid="onboarding-travel-scope-select" className="bg-white/5 border-white/10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10 text-slate-200">
                  {TRAVEL_OPTS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Performance languages (comma-sep)</Label>
              <Input value={form.performance_languages} onChange={update('performance_languages')} placeholder="Hindi, English, Tamil" data-testid="onboarding-perf-languages-input" className="bg-white/5 border-white/10 mt-1" />
            </div>
          </div>
        </div>

        {/* Milestones & Achievements */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <h2 className="font-display text-xl text-white flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /> Milestones &amp; achievements</h2>
            <Button type="button" onClick={addMilestone} variant="outline" data-testid="onboarding-add-milestone" className="border-white/10 bg-white/5 hover:bg-white/10 text-slate-200 rounded-full">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add milestone
            </Button>
          </div>
          {milestones.length === 0 && <div className="text-sm text-slate-500 italic">Tell hosts what you&apos;re proud of — awards, sold-out shows, brand campaigns, OTT credits.</div>}
          <div className="space-y-4">
            {milestones.map((m, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="grid sm:grid-cols-[112px_1fr_auto] gap-3 items-start">
                  <div className="aspect-square w-28 rounded-lg bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center relative">
                    {m.image_url ? (
                      <img src={m.image_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Trophy className="h-7 w-7 text-slate-600" />
                    )}
                    <label className="absolute inset-0 cursor-pointer opacity-0 hover:opacity-100 hover:bg-black/40 transition-all flex items-center justify-center">
                      <span className="text-[10px] uppercase tracking-widest text-white flex items-center gap-1"><ImagePlus className="h-3 w-3" /> upload</span>
                      <input type="file" accept="image/*" hidden onChange={(e) => upload(e, `milestone-${i}`)} disabled={uploading} />
                    </label>
                  </div>
                  <div className="space-y-2">
                    <Input value={m.title} onChange={(e) => updateMilestone(i, 'title', e.target.value)} placeholder="e.g. Headlined Sunburn 2024" data-testid={`onboarding-milestone-title-${i}`} className="bg-white/5 border-white/10" />
                    <Textarea value={m.description} onChange={(e) => updateMilestone(i, 'description', e.target.value)} rows={2} placeholder="Short description (optional)" data-testid={`onboarding-milestone-desc-${i}`} className="bg-white/5 border-white/10" />
                    <Input value={m.year} onChange={(e) => updateMilestone(i, 'year', e.target.value)} placeholder="Year (optional)" data-testid={`onboarding-milestone-year-${i}`} className="bg-white/5 border-white/10 max-w-[140px]" />
                  </div>
                  <button type="button" onClick={() => removeMilestone(i)} aria-label="Remove" className="text-slate-500 hover:text-rose-300 transition-colors p-2">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Gallery / Portfolio */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><ImagePlus className="h-5 w-5 text-fuchsia-300" /> Gallery</h2>
          <p className="text-xs text-slate-400 mb-4">Upload past performance photos to add credibility. They&apos;ll appear in your public profile gallery.</p>
          {portfolio.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
              {portfolio.map((src, i) => (
                <div key={src} className="relative group aspect-square">
                  <img src={src} alt="" className="w-full h-full object-cover rounded-lg border border-white/10" />
                  <button type="button" onClick={() => removePortfolio(i)} aria-label="Remove" className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <label className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200 cursor-pointer">
            <ImagePlus className="h-4 w-4" /> Add gallery photo
            <input type="file" accept="image/*" hidden onChange={(e) => upload(e, 'portfolio')} disabled={uploading} />
          </label>
        </div>

        {/* Past Performance Videos */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <h2 className="font-display text-xl text-white mb-2 flex items-center gap-2"><Youtube className="h-5 w-5 text-rose-400" /> Past performance videos</h2>
          <p className="text-xs text-slate-400 mb-3">Paste 6–8 YouTube links (one per line). Thumbnails will load on demand to keep the page fast.</p>
          <Textarea value={form.embed_input} onChange={update('embed_input')} rows={5} placeholder="https://youtube.com/watch?v=…&#10;https://youtu.be/…" data-testid={ONBOARDING.embedInput} className="bg-white/5 border-white/10" />
        </div>

        {/* Testimonial Videos */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <h2 className="font-display text-xl text-white mb-2 flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-300" /> Testimonial videos</h2>
          <p className="text-xs text-slate-400 mb-3">YouTube/Vimeo links from past clients (one per line). Loaded only when a host clicks play.</p>
          <Textarea value={form.testimonial_input} onChange={update('testimonial_input')} rows={4} placeholder="https://youtube.com/watch?v=…" data-testid="onboarding-testimonial-input" className="bg-white/5 border-white/10" />
        </div>

        {/* Social media + followers */}
        <div className="glass rounded-2xl p-7 border border-white/10">
          <h2 className="font-display text-xl text-white mb-2">Social media presence</h2>
          <p className="text-xs text-slate-400 mb-4">Your follower counts will be shown on your public profile (icons only — links won&apos;t be clickable for privacy &amp; platform integrity).</p>
          <div className="space-y-3">
            {SOCIAL_PLATFORMS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.id} className="grid grid-cols-[40px_1fr_140px_auto] items-center gap-2">
                  <Icon className={`h-5 w-5 ${p.color}`} />
                  <Input
                    value={form.social_links[p.id] || ''}
                    onChange={updateSocial(p.id, 'url')}
                    placeholder={`${p.label} URL`}
                    data-testid={`onboarding-social-url-${p.id}`}
                    className="bg-white/5 border-white/10"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={form.social_followers[p.id] || 0}
                    onChange={updateSocial(p.id, 'followers')}
                    placeholder={p.followerLabel}
                    data-testid={`onboarding-social-followers-${p.id}`}
                    className="bg-white/5 border-white/10 text-right"
                  />
                  <VerifySocialButton
                    platform={p.id}
                    declared={form.social_followers[p.id] || 0}
                    status={socialVerified[p.id]}
                    onChange={(s) => setSocialVerified((sv) => ({ ...sv, [p.id]: s }))}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Calendar Manager */}
        {artistId && (
          <div className="glass rounded-2xl p-7 border border-white/10">
            <h2 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-300" /> Availability calendar</h2>
            <CalendarManager artistId={artistId} />
          </div>
        )}

        {/* Bio + Details */}
        <div className="glass rounded-2xl p-7 border border-white/10 space-y-4">
          <h2 className="font-display text-xl text-white mb-2">Profile details</h2>
          <div>
            <Label className="text-xs text-slate-400 uppercase tracking-wider">Bio</Label>
            <Textarea value={form.bio} onChange={update('bio')} rows={5} data-testid={ONBOARDING.bioInput} className="bg-white/5 border-white/10 mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Skills (comma-separated)</Label>
              <Input value={form.skills} onChange={update('skills')} data-testid={ONBOARDING.skillsInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Languages</Label>
              <Input value={form.languages} onChange={update('languages')} data-testid={ONBOARDING.languagesInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">City</Label>
              <Input value={form.location_city} onChange={update('location_city')} data-testid={ONBOARDING.cityInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Country</Label>
              <Input value={form.location_country} onChange={update('location_country')} data-testid={ONBOARDING.countryInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400 uppercase tracking-wider">Experience (years)</Label>
              <Input type="number" min="0" value={form.experience_years} onChange={update('experience_years')} data-testid={ONBOARDING.experienceInput} className="bg-white/5 border-white/10 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">Price min (₹)</Label>
                <Input type="number" min="0" value={form.price_min} onChange={update('price_min')} data-testid={ONBOARDING.priceMinInput} className="bg-white/5 border-white/10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-400 uppercase tracking-wider">Price max (₹)</Label>
                <Input type="number" min="0" value={form.price_max} onChange={update('price_max')} data-testid={ONBOARDING.priceMaxInput} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </div>
          </div>
        </div>

        <Button type="submit" disabled={loading} data-testid={ONBOARDING.saveButton} className="w-full btn-rainbow text-white border-0 rounded-xl py-6 text-base">
          {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : 'Save artist profile'}
        </Button>
      </form>
    </div>
  );
};

export default Onboarding;
